import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-input-preview',
  templateUrl: './input-preview.component.html',
  styleUrls: ['./input-preview.component.scss'],
  providers: [ConfirmationService],
})
export class InputPreviewComponent {
  @Input() fields: any[] | null = [];
  /** Which section this preview belongs to (for future use; parent wires edit/remove). */
  @Input() sectionKind: 'user' | 'provider' = 'user';
  @Output() fieldChange = new EventEmitter<any>();
  @Output() fieldEditRequest = new EventEmitter<any>();
  @Output() fieldToRemove = new EventEmitter<any>();

  private static readonly FILE_ACCEPT: Record<string, string> = {
    image: 'image/*,.jpg,.jpeg,.png,.gif,.webp,.heic',
    pdf: '.pdf,application/pdf',
    documents:
      '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    excel:
      '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };

  constructor(private confirmationService: ConfirmationService) {}

  trackByFieldId = (_i: number, f: any): string => f?.fieldId ?? String(_i);

  fileAcceptAttr(field: any): string {
    const cats: string[] = Array.isArray(field?.fileAcceptTypes)
      ? field.fileAcceptTypes
      : ['image'];
    const parts = cats
      .map((c) => InputPreviewComponent.FILE_ACCEPT[c])
      .filter(Boolean);
    return parts.length
      ? parts.join(',')
      : InputPreviewComponent.FILE_ACCEPT.image;
  }

  getOptions(field: any): any[] {
    const ds = field?.optionsDataset;
    if (Array.isArray(ds) && ds[0]?.options?.length) {
      return ds[0].options;
    }
    return [];
  }

  hasOptionList(field: any): boolean {
    return this.getOptions(field).length > 0;
  }

  emitPatch(field: any, partial: Record<string, unknown>): void {
    this.fieldChange.emit({ ...field, ...partial });
  }

  textLikeValue(field: any): string {
    const v = field?.defaultValue;
    if (v != null && v !== '') {
      return String(v);
    }
    return '';
  }

  onFileChosen(field: any, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.emitPatch(field, { defaultValue: file ? file.name : '' });
  }

  requestEdit(field: any, ev: Event): void {
    ev.stopPropagation();
    this.fieldEditRequest.emit(field);
  }

  confirmRemove(event: Event, field: any): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Remove this field from the form?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.fieldToRemove.emit(field),
      reject: () => {},
    });
  }

  toggleMultiCheckbox(field: any, optionValue: any, checked: boolean): void {
    const cur = Array.isArray(field.defaultValue) ? [...field.defaultValue] : [];
    const i = cur.indexOf(optionValue);
    if (checked && i < 0) {
      cur.push(optionValue);
    }
    if (!checked && i >= 0) {
      cur.splice(i, 1);
    }
    this.emitPatch(field, { defaultValue: cur });
  }

  isOptionChecked(field: any, optionValue: any): boolean {
    const cur = field?.defaultValue;
    return Array.isArray(cur) && cur.includes(optionValue);
  }

  /** Single-select from dataset using checkbox UI (only one checked at a time). */
  toggleExclusiveCheckbox(
    field: any,
    optionValue: any,
    checked: boolean
  ): void {
    if (checked) {
      this.emitPatch(field, { defaultValue: optionValue });
      return;
    }
    if (this.isExclusiveChecked(field, optionValue)) {
      this.emitPatch(field, { defaultValue: null });
    }
  }

  isExclusiveChecked(field: any, optionValue: any): boolean {
    const dv = field?.defaultValue;
    return dv === optionValue || dv == optionValue;
  }
}
